import { IonIcon } from '@ionic/react';
import { backspaceOutline } from 'ionicons/icons';
import React from 'react';

import { ReactAriaButton } from './button';

const stripNonDigits = (str: string): string => {
  return str.replace(/\D/g, '');
};

const formatPhoneNumber = (str: string | null): string => {
  if (!str) {
    return '';
  }
  if (str.length === 0) {
    return '';
  }
  if (str.length < 4) {
    return `(${str}`;
  }
  if (str.length < 7) {
    return `(${str.slice(0, 3)}) ${str.slice(3)}`;
  }
  return `(${str.slice(0, 3)}) ${str.slice(3, 6)}-${str.slice(6, 10)}`;
};

const PhonePadButton: React.FC<{
  onClick: () => void;
  numberLabel?: string;
  icon?: string;
  iconColor?: string;
  className?: string;
}> = ({ onClick, numberLabel, icon, iconColor, className }) => {
  return (
    <ReactAriaButton
      onPress={onClick}
      className={`tw-m-1 tw-flex tw-h-[58px] tw-w-[58px] tw-items-center tw-justify-center tw-rounded-[50%] tw-border tw-border-solid tw-border-[#999999] tw-bg-white tw-p-1 tw-text-center tw-text-4xl active:tw-bg-[#E6E6E6] ${
        className ?? ''
      }`}
    >
      {numberLabel || (
        <IonIcon
          icon={icon}
          color={iconColor}
        />
      )}
    </ReactAriaButton>
  );
};

interface PhonePadProps {
  value: string | null;
  setValue: (str: string) => void;
  credentialsError?: boolean;
}

const PhonePadRow: React.FC<{
  children: React.ReactNode;
  twJustifyContent?: string;
}> = ({ children, twJustifyContent = 'tw-justify-between' }) => {
  return (
    <div className={`tw-flex tw-flex-row ${twJustifyContent} tw-px-5 tw-py-0`}>{children}</div>
  );
};

export const PhonePad: React.FC<PhonePadProps> = ({
  value,
  setValue,
  credentialsError,
}): React.ReactNode => {
  const appendToPhoneNumberIfValid = React.useCallback(
    (digit: number): void => {
      if (value && value.length < 14) {
        setValue(formatPhoneNumber(`${stripNonDigits(value)}${digit}`));
      } else {
        setValue(formatPhoneNumber(`${digit}`));
      }
    },
    [value, setValue],
  );

  const removeLastDigit = React.useCallback((): void => {
    if (value?.length) {
      setValue(formatPhoneNumber(stripNonDigits(value).slice(0, -1)));
    }
  }, [value, setValue]);

  React.useEffect(() => {
    if (!window.isKioskPreview) {
      return;
    }

    const handleKeyPress = (event: KeyboardEvent): void => {
      if (event.key >= '0' && event.key <= '9') {
        event.preventDefault();
        appendToPhoneNumberIfValid(parseInt(event.key));
      } else if (event.key === 'Backspace') {
        event.preventDefault();
        removeLastDigit();
      } else if (event.key === 'Enter') {
        event.preventDefault();
        const form = document.querySelector('form');
        if (form) {
          form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
        }
      }
    };

    document.addEventListener('keydown', handleKeyPress);

    return () => {
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, [appendToPhoneNumberIfValid, removeLastDigit]);

  return (
    <React.Fragment key={'phone-pad'}>
      <div className={`tw-w-full tw-text-center tw-text-5xl`}>
        <input
          type="tel"
          value={value || ''}
          placeholder={'(000) 000-0000'}
          className="phone-number tw-w-full tw-border-none tw-text-center tw-text-5xl"
          disabled={true}
        />
        {credentialsError && (
          <p
            className="tw-m-auto tw-w-full tw-px-2 tw-text-center tw-text-sm tw-text-red-600"
            slot="error"
          >
            Invalid Phone Number
          </p>
        )}
      </div>

      <div className="tw-m-auto tw-w-[250px] tw-justify-center tw-pt-8">
        <PhonePadRow>
          <PhonePadButton
            onClick={() => appendToPhoneNumberIfValid(1)}
            numberLabel="1"
          />
          <PhonePadButton
            onClick={() => appendToPhoneNumberIfValid(2)}
            numberLabel="2"
          />
          <PhonePadButton
            onClick={() => appendToPhoneNumberIfValid(3)}
            numberLabel="3"
          />
        </PhonePadRow>
        <PhonePadRow>
          <PhonePadButton
            onClick={() => appendToPhoneNumberIfValid(4)}
            numberLabel="4"
          />
          <PhonePadButton
            onClick={() => appendToPhoneNumberIfValid(5)}
            numberLabel="5"
          />
          <PhonePadButton
            onClick={() => appendToPhoneNumberIfValid(6)}
            numberLabel="6"
          />
        </PhonePadRow>
        <PhonePadRow>
          <PhonePadButton
            onClick={() => appendToPhoneNumberIfValid(7)}
            numberLabel="7"
          />
          <PhonePadButton
            onClick={() => appendToPhoneNumberIfValid(8)}
            numberLabel="8"
          />
          <PhonePadButton
            onClick={() => appendToPhoneNumberIfValid(9)}
            numberLabel="9"
          />
        </PhonePadRow>
        <PhonePadRow twJustifyContent="tw-justify-end">
          <PhonePadButton
            onClick={() => appendToPhoneNumberIfValid(0)}
            numberLabel="0"
            className="tw-mr-2"
          />
          <PhonePadButton
            onClick={() => removeLastDigit()}
            icon={backspaceOutline}
            iconColor={'black'}
          />
        </PhonePadRow>
      </div>
    </React.Fragment>
  );
};
